import styled from 'styled-components';

import { Text } from '@/nova/components';
import { CUST, CUST_ANIM } from '../../../../theme/customizer-tokens';
import { SPACE } from '../../../../theme/tokens';

export const Wrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;
`;

export const Trigger = styled.button`
  display: flex;
  align-items: stretch;
  gap: ${SPACE[8]};
  width: 100%;
  min-height: 64px;
  padding: 10px ${SPACE[12]};
  border: 1px solid ${CUST.fieldBorder};
  border-radius: 12px;
  background: ${CUST.fieldBg};
  cursor: pointer;
  text-align: left;
`;

export const TriggerMain = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${SPACE[4]};
  padding: 0 6px;
`;

export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[2]};
`;

export const LabelText = styled(Text)`
  font-size: 16px;
  line-height: 24px;
  color: ${CUST.textLabel};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Req = styled.span`
  color: ${CUST.required};
  font-size: 16px;
  line-height: 24px;
`;

export const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  width: 100%;
  min-width: 0;
`;

export const ValueName = styled(Text)`
  flex: 0 1 auto;
  min-width: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  color: ${CUST.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ValueCurrency = styled(Text)`
  flex: 0 0 auto;
  margin-left: auto;
  padding-left: ${SPACE[8]};
  font-size: 16px;
  line-height: 24px;
  color: ${CUST.textPlaceholder};
  white-space: nowrap;
`;

export const Placeholder = styled(Text)`
  font-size: 16px;
  line-height: 24px;
  color: ${CUST.textPlaceholder};
  white-space: nowrap;
`;

export const Chevron = styled.span<{ $open: boolean }>`
  flex: 0 0 auto;
  align-self: center;
  display: inline-flex;
  color: ${CUST.textPlaceholder};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform ${CUST_ANIM.INTERACT_MS}ms ease;
`;

/** Fixed flag slot — always rendered (even when a region has no flag) so every
 *  row's name aligns on the same x. */
export const FlagBox = styled.span`
  flex: 0 0 auto;
  width: 24px;
  height: 18px;
  border-radius: 3px;
  overflow: hidden;
  background: ${CUST.flagRing};
`;

export const RowFlagImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const Menu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  max-height: 360px;
  overflow-y: auto;
  padding: ${SPACE[8]};
  border: 1px solid ${CUST.menuBorder};
  border-radius: 12px;
  background: ${CUST.menuBg};
  box-shadow: ${CUST.menuShadow};
`;

export const SearchWrap = styled.label`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[8]} 10px;
  margin-bottom: ${SPACE[4]};
  border: 1px solid ${CUST.menuBorder};
  border-radius: 8px;
  background: ${CUST.menuBg};
  color: ${CUST.textPlaceholder};

  /* The search drops the browser's outline, so its box says it has the focus. */
  &:focus-within {
    border-color: ${CUST.accent};
  }
`;

export const SearchInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${CUST.white};
  font-size: 14px;

  &::placeholder {
    color: ${CUST.textPlaceholder};
  }
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};

  /* Divider line between region groups (Figma/old impl). */
  &:not(:first-of-type) {
    margin-top: 6px;
    padding-top: 10px;
    border-top: 1px solid ${CUST.menuBorder};
  }
`;

export const GroupName = styled(Text)`
  padding: 6px 10px ${SPACE[4]};
  font-size: 12px;
  line-height: 16px;
  color: ${CUST.textMuted};
`;

export const Row = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 10px;
  border: none;
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? CUST.rowHover : 'transparent')};
  transition: background ${CUST_ANIM.INTERACT_MS}ms ease;

  /* Country/region flags are smaller in the menu rows (Figma 14×10) than in the
     trigger's selected-value row, so size the shared FlagBox down here. */
  ${FlagBox} {
    width: 16px;
    height: 12px;
  }

  &:hover:not(:disabled) {
    background: ${CUST.rowHover};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const RowName = styled(Text)`
  flex: 0 1 auto;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${CUST.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SoonPill = styled(Text)`
  flex: 0 0 auto;
  padding: ${SPACE[2]} ${SPACE[8]};
  border-radius: 9999px;
  background: ${CUST.soonPillBg};
  color: ${CUST.comingSoon};
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
`;

export const RowCurrency = styled(Text)`
  flex: 0 0 auto;
  margin-left: auto;
  padding-left: ${SPACE[12]};
  font-size: 12px;
  line-height: 16px;
  color: ${CUST.rowCurrency};
  white-space: nowrap;
`;

export const Empty = styled(Text)`
  padding: ${SPACE[12]} 10px;
  font-size: 14px;
  color: ${CUST.textMuted};
`;